import type { CellContext } from '@tanstack/react-table';
import { useUpdateFeatureFlagsMutation } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api';
import type { FetchFeatureFlagsResponseData } from 'gcs-common/clients/api/entities/feature-flags/feature-flags-api-helpers';
import { fetchHouseSelectOptions } from 'gcs-common/slices/administration/houses/housesThunks';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { GroupBase } from 'react-select';
import AsyncSelect, { type AsyncProps } from 'react-select/async';
import { useDebouncedCallback } from 'use-debounce';
import EditingCell from '../EditingCell/EditingCell';

interface OptionType {
  value: string;
  label: string;
}

type LoadOptionsParameters = Parameters<
  NonNullable<AsyncProps<OptionType, true, GroupBase<OptionType>>['loadOptions']>
>;

interface FeatureFlagHousesCellProps {
  cell: CellContext<FetchFeatureFlagsResponseData['data'][number], unknown>['cell'];
  value: FetchFeatureFlagsResponseData['data'][number]['houses'];
}

const FeatureFlagHousesCell = (props: FeatureFlagHousesCellProps) => {
  const { cell, value: houses } = props;
  const dispatch = useDispatch();
  const { mutateAsync, isPending } = useUpdateFeatureFlagsMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);

  const featureFlagId = cell.row.original.id;

  const fetchOptions = async (...args: LoadOptionsParameters) => {
    const [search, callbackFn] = args;
    const houseSelectOptions = await dispatch(fetchHouseSelectOptions(search));

    callbackFn(
      // @ts-expect-error // TODO refactor soon
      houseSelectOptions.map((house) => ({
        label: house.friendlyName,
        value: house.id,
      })),
    );
  };

  const [fetchOptionsDebounced] = useDebouncedCallback<LoadOptionsParameters>((...args) => {
    return fetchOptions(...args);
  }, 500);

  const setInitialOptions = () => {
    const initialOptions = houses.map((house) => ({ label: house.friendlyName, value: house.id }));
    setOptions(initialOptions);
  };

  useEffect(() => {
    setInitialOptions();
  }, []);

  const save = async () => {
    const houseIds = options?.map((option) => option.value) || [];

    await mutateAsync({
      path: {
        feature_flag_id: featureFlagId,
      },
      body: {
        houseIds,
      },
    });
  };

  const cancel = () => {
    setInitialOptions();
  };

  return (
    <EditingCell disabled={isPending} onSave={save} isEditing={isEditing} setIsEditing={setIsEditing} onCancel={cancel}>
      <AsyncSelect<OptionType, true>
        isMulti
        loadOptions={fetchOptionsDebounced}
        isDisabled={!isEditing || isPending}
        value={options}
        isLoading={isPending}
        onChange={(selected) => {
          setOptions([...selected]);
        }}
        styles={{ container: (base) => ({ ...base, minWidth: '300px' }) }}
      />
    </EditingCell>
  );
};

export default FeatureFlagHousesCell;
